import { supabase } from '../client';
import type { MatchResult } from '@modules/smart-match/types';

const SELECT_LIST = `
  *,
  candidate:ggj_b2b_candidates(*),
  job:ggj_b2b_jobs(*, client:ggj_b2b_clients(id, name))
`;

export interface MatchResultFilters {
  jobId?: string;
  candidateId?: string;
}

export interface MatchKPIs {
  totalMatches: number;
  avgScore: number;
  strongCount: number;
  goodCount: number;
  partialCount: number;
  /** Open jobs with a hiring deadline within the next 14 days (or overdue). */
  urgentOpenJobsCount: number;
}

export const matchResultService = {
  async upsert(
    orgId: string,
    data: {
      candidate_id: string;
      job_id: string;
      composite_score: number;
      dimension_scores: Record<string, number>;
      matched_skills: string[];
      missing_skills: string[];
      bonus_skills: string[];
      recommendation: string;
      analysis_json?: Record<string, unknown>;
      llm_confidence?: number;
      key_strengths?: string[];
      key_concerns?: string[];
      match_version?: number;
    }
  ): Promise<void> {
    const { data: existing, error: readErr } = await supabase
      .from('ggj_b2b_match_results')
      .select('is_preserved')
      .eq('org_id', orgId)
      .eq('candidate_id', data.candidate_id)
      .eq('job_id', data.job_id)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (existing && (existing as { is_preserved?: boolean }).is_preserved === true) return;

    const { error } = await supabase
      .from('ggj_b2b_match_results')
      .upsert(
        { org_id: orgId, ...data, matched_at: new Date().toISOString() },
        { onConflict: 'org_id,candidate_id,job_id' }
      );
    if (error) throw new Error(error.message);
  },

  async setPreserved(
    orgId: string,
    candidateId: string,
    jobId: string,
    preserved: boolean
  ): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_match_results')
      .update({ is_preserved: preserved })
      .eq('org_id', orgId)
      .eq('candidate_id', candidateId)
      .eq('job_id', jobId);
    if (error) throw new Error(error.message);
  },

  /** Deletes all rows for the org where is_preserved is false. Returns deleted count when available. */
  async clearUnpreservedForOrg(orgId: string): Promise<number> {
    const { error, count } = await supabase
      .from('ggj_b2b_match_results')
      .delete({ count: 'exact' })
      .eq('org_id', orgId)
      .eq('is_preserved', false);
    if (error) throw new Error(error.message);
    return count ?? 0;
  },

  /** Deletes unpreserved rows for one job (e.g. from job detail Matches tab). */
  async clearUnpreservedForJob(orgId: string, jobId: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_match_results')
      .delete()
      .eq('org_id', orgId)
      .eq('job_id', jobId)
      .eq('is_preserved', false);
    if (error) throw new Error(error.message);
  },

  async clearUnpreservedForCandidate(orgId: string, candidateId: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_match_results')
      .delete()
      .eq('org_id', orgId)
      .eq('candidate_id', candidateId)
      .eq('is_preserved', false);
    if (error) throw new Error(error.message);
  },

  async upsertBatch(
    orgId: string,
    rows: Parameters<typeof matchResultService.upsert>[1][]
  ): Promise<void> {
    for (const row of rows) {
      await matchResultService.upsert(orgId, row);
    }
  },

  async list(orgId: string, filters?: MatchResultFilters): Promise<MatchResult[]> {
    let query = supabase
      .from('ggj_b2b_match_results')
      .select(SELECT_LIST)
      .eq('org_id', orgId)
      .order('composite_score', { ascending: false })
      .limit(200);

    if (filters?.jobId) query = query.eq('job_id', filters.jobId);
    if (filters?.candidateId) query = query.eq('candidate_id', filters.candidateId);

    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as MatchResult[];
  },

  async getForJob(orgId: string, jobId: string, limit = 10): Promise<MatchResult[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_match_results')
      .select(
        `*, candidate:ggj_b2b_candidates(*), job:ggj_b2b_jobs(*, client:ggj_b2b_clients(id, name))`
      )
      .eq('org_id', orgId)
      .eq('job_id', jobId)
      .order('composite_score', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as MatchResult[];
  },

  async getForCandidate(orgId: string, candidateId: string, limit = 10): Promise<MatchResult[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_match_results')
      .select(`*, job:ggj_b2b_jobs(*, client:ggj_b2b_clients(id, name))`)
      .eq('org_id', orgId)
      .eq('candidate_id', candidateId)
      .order('composite_score', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as MatchResult[];
  },

  /**
   * Batched variant of `getForCandidate` — fetches historical match rows
   * for a *set* of candidates in a single SQL round-trip.
   *
   * Used by `MatchStackColumn` to populate the `Also a fit for` footer
   * when the current search's N×M alternates are empty (single-JD flow).
   * We group + sort client-side so the returned map is ready to render.
   *
   * @param perCandidateLimit  Slice per candidate; default 5 so cards
   *   never explode vertically.
   */
  async getForCandidates(
    orgId: string,
    candidateIds: string[],
    perCandidateLimit = 5,
  ): Promise<Map<string, MatchResult[]>> {
    const out = new Map<string, MatchResult[]>();
    const ids = Array.from(new Set(candidateIds.filter(Boolean)));
    if (ids.length === 0) return out;
    const { data, error } = await supabase
      .from('ggj_b2b_match_results')
      .select(`*, job:ggj_b2b_jobs(*, client:ggj_b2b_clients(id, name))`)
      .eq('org_id', orgId)
      .in('candidate_id', ids)
      .order('composite_score', { ascending: false })
      .limit(ids.length * perCandidateLimit * 2);
    if (error) throw new Error(error.message);
    for (const row of (data ?? []) as unknown as MatchResult[]) {
      const list = out.get(row.candidate_id) ?? [];
      if (list.length < perCandidateLimit) {
        list.push(row);
        out.set(row.candidate_id, list);
      }
    }
    return out;
  },

  async deleteForJob(orgId: string, jobId: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_match_results')
      .delete()
      .eq('org_id', orgId)
      .eq('job_id', jobId);
    if (error) throw new Error(error.message);
  },

  async getKPIs(orgId: string): Promise<MatchKPIs> {
    const { data, error } = await supabase
      .from('ggj_b2b_match_results')
      .select('composite_score, recommendation')
      .eq('org_id', orgId);

    if (error) throw new Error(error.message);

    const rows = data ?? [];
    const totalMatches = rows.length;
    const avgScore =
      totalMatches > 0
        ? Math.round(rows.reduce((sum, r) => sum + (r.composite_score ?? 0), 0) / totalMatches)
        : 0;
    const strongCount = rows.filter((r) => (r.composite_score ?? 0) >= 75).length;
    const goodCount = rows.filter(
      (r) => (r.composite_score ?? 0) >= 55 && (r.composite_score ?? 0) < 75
    ).length;
    const partialCount = rows.filter(
      (r) => (r.composite_score ?? 0) >= 35 && (r.composite_score ?? 0) < 55
    ).length;

    const horizon = new Date();
    horizon.setDate(horizon.getDate() + 14);

    const { count: urgentOpenJobsCount, error: urgentErr } = await supabase
      .from('ggj_b2b_jobs')
      .select('*', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('status', 'open')
      .not('hiring_deadline', 'is', null)
      .lte('hiring_deadline', horizon.toISOString());

    if (urgentErr) {
      console.warn('[matchResultService.getKPIs] urgent jobs count failed:', urgentErr.message);
    }

    return {
      totalMatches,
      avgScore,
      strongCount,
      goodCount,
      partialCount,
      urgentOpenJobsCount: urgentOpenJobsCount ?? 0,
    };
  },
};
